import React, { useState } from 'react';
import { 
  Lightbulb, 
  Plus, 
  ThumbsUp, 
  ThumbsDown, 
  MessageSquare,
  Star,
  TrendingUp,
  Filter,
  Search,
  User,
  Calendar,
  Tag
} from 'lucide-react';

const BoiteAIdees = () => {
  const [newIdea, setNewIdea] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [filter, setFilter] = useState('all');

  // Données d'exemple pour les idées
  const [ideas, setIdeas] = useState([
    {
      id: 1,
      title: 'Application mobile pour les clients',
      description: 'Développer une app pour que les clients puissent suivre leur installation en temps réel',
      author: 'Commercial A',
      date: '2025-01-05',
      category: 'Innovation',
      likes: 12,
      dislikes: 2,
      comments: 5,
      status: 'new',
      priority: 'high'
    },
    {
      id: 2,
      title: 'Système de parrainage clients',
      description: 'Mettre en place un programme de parrainage avec récompenses pour les clients qui nous recommandent',
      author: 'Manager Vente',
      date: '2025-01-04',
      category: 'Marketing',
      likes: 8,
      dislikes: 1,
      comments: 3,
      status: 'in-review',
      priority: 'medium'
    },
    {
      id: 3,
      title: 'Optimisation des tournées techniques',
      description: 'Utiliser un algorithme pour optimiser les trajets des équipes techniques et réduire les coûts',
      author: 'Technicien Senior',
      date: '2025-01-03',
      category: 'Logistique',
      likes: 15,
      dislikes: 0,
      comments: 7,
      status: 'approved',
      priority: 'high'
    }
  ]);

  const categories = ['Innovation', 'Marketing', 'Logistique', 'RH', 'Finance', 'Autre'];

  const getStatusColor = (status) => {
    switch(status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'in-review': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'new': return 'Nouvelle';
      case 'in-review': return 'En révision';
      case 'approved': return 'Approuvée';
      case 'rejected': return 'Rejetée';
      default: return 'Inconnue';
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'border-red-300 bg-red-50';
      case 'medium': return 'border-yellow-300 bg-yellow-50';
      case 'low': return 'border-green-300 bg-green-50';
      default: return 'border-gray-300 bg-gray-50';
    }
  };

  const handleSubmitIdea = () => {
    if (newIdea.trim() && selectedCategory) {
      const idea = {
        id: ideas.length + 1,
        title: newIdea,
        description: newIdea,
        author: 'Vous',
        date: new Date().toISOString().split('T')[0],
        category: selectedCategory,
        likes: 0,
        dislikes: 0,
        comments: 0,
        status: 'new',
        priority: 'medium'
      };
      setIdeas([idea, ...ideas]);
      setNewIdea('');
      setSelectedCategory('');
    }
  };

  const filteredIdeas = ideas.filter(idea => {
    if (filter === 'all') return true;
    return idea.status === filter;
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center">
            <Lightbulb className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Boîte à idées</h1>
            <p className="text-gray-600">Partagez vos idées d'amélioration</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            <span className="font-semibold">{ideas.length}</span> idées soumises
          </div>
        </div>
      </div>

      {/* Nouvelle idée */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-orange-500" />
          Soumettre une nouvelle idée
        </h2>
        
        <div className="space-y-4">
          <div>
            <textarea
              value={newIdea}
              onChange={(e) => setNewIdea(e.target.value)}
              placeholder="Décrivez votre idée en détail..."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none h-24"
            />
          </div>
          
          <div className="flex gap-4">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">Choisir une catégorie</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            
            <button
              onClick={handleSubmitIdea}
              disabled={!newIdea.trim() || !selectedCategory}
              className="px-6 py-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-xl font-medium hover:from-orange-600 hover:to-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              Soumettre l'idée
            </button>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Filtrer par statut :</span>
        </div>
        
        <div className="flex gap-2">
          {[
            { key: 'all', label: 'Toutes' },
            { key: 'new', label: 'Nouvelles' },
            { key: 'in-review', label: 'En révision' },
            { key: 'approved', label: 'Approuvées' }
          ].map(status => (
            <button
              key={status.key}
              onClick={() => setFilter(status.key)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                filter === status.key
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {/* Liste des idées */}
      <div className="space-y-4">
        {filteredIdeas.map(idea => (
          <div key={idea.id} className={`bg-white rounded-2xl shadow-sm border-l-4 p-6 ${getPriorityColor(idea.priority)}`}>
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">{idea.title}</h3>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(idea.status)}`}>
                  {getStatusLabel(idea.status)}
                </span>
              </div>
            </div>
            
            <p className="text-gray-600 mb-4">{idea.description}</p>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {idea.author}
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(idea.date).toLocaleDateString('fr-FR')}
                </div>
                <div className="flex items-center gap-1">
                  <Tag className="w-4 h-4" />
                  {idea.category}
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-1 text-green-600 hover:text-green-700 transition-colors">
                  <ThumbsUp className="w-4 h-4" />
                  <span className="text-sm font-medium">{idea.likes}</span>
                </button>
                <button className="flex items-center gap-1 text-red-600 hover:text-red-700 transition-colors">
                  <ThumbsDown className="w-4 h-4" />
                  <span className="text-sm font-medium">{idea.dislikes}</span>
                </button>
                <button className="flex items-center gap-1 text-blue-600 hover:text-blue-700 transition-colors">
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-sm font-medium">{idea.comments}</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BoiteAIdees;